import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { VStack } from '@components/base/layout';
import { Text, Icon } from '@components/base/display';
import { RuleContextProvider } from '@components/shared/configurable';
import type { RuleContextValue } from '@components/shared/configurable';
import { DocumentsCardConfigurable as DocumentsCard } from '../components/register';
import {
  buildPopoutHeaderTitle,
  usePopoutClaimListener,
  type DocumentsPopoutClaim,
} from '../components/documents/documentsPopout';

const EMPTY_RULE_CONTEXT: RuleContextValue = {};

export function DocumentsPopoutPage() {
  const [searchParams] = useSearchParams();
  const { taskId } = useParams<{ tenantSlug: string; taskId: string }>();

  const [claim, setClaim] = useState<DocumentsPopoutClaim>(() => ({
    ses: searchParams.get('ses') ?? '',
    idSin: searchParams.get('id_sin') ?? '',
    taskId,
    // Header metadata seeded from the URL so the title shows on first open; the
    // broadcast then keeps it fresh as the main window switches tasks.
    dossierNumber: searchParams.get('dossier') ?? undefined,
    contractRef: searchParams.get('contract') ?? undefined,
    client: searchParams.get('client') ?? undefined,
  }));

  // Follow the main window's active task (ses/idSin + header metadata).
  usePopoutClaimListener(setClaim);

  useEffect(() => {
    // Prefer the dossier number in the tab title (matches the mockup:
    // "Documents · 25700751"); fall back to the task id, then a bare label.
    if (claim.dossierNumber) document.title = `Documents · ${claim.dossierNumber}`;
    else document.title = claim.taskId ? `Documents — Tâche ${claim.taskId}` : 'Documents';
  }, [claim.dossierNumber, claim.taskId]);

  const ruleContext = useMemo(() => EMPTY_RULE_CONTEXT, []);
  const hasClaim = claim.ses !== '' && claim.idSin !== '';

  return (
    <RuleContextProvider value={ruleContext}>
      {/* The popout renders outside `ClaimFlowLayout`, so it owns its scroll
          container: `#root` is `overflow: hidden` (see index.css) and the app
          always scrolls inside an inner region. Height is viewport-based rather
          than `h-full` because `ClaimFlowAuthGuard` / `IntegratedComponentProvider`
          sit in between and do not carry a percentage height chain. */}
      <div className="h-screen overflow-y-auto bg-secondary-50 p-4">
        {hasClaim ? (
          <VStack gap={4}>
            {/* Page header — dossier / contract references + the claim's
                client (the insured, PASS_NOM_PER), above the card (bespoke
                chrome, mirrors ClaimSummaryCard's client/dossier bar; not part
                of the configurable field set). */}
            <div className="flex items-center gap-3 px-1">
              <Icon icon={FileText} size="lg" color="primary" />
              <div className="min-w-0">
                <Text as="h1" size="lg" weight="bold" className="truncate">
                  {buildPopoutHeaderTitle(claim)}
                </Text>
                {claim.client && (
                  <Text
                    as="p"
                    size="xs"
                    color="muted"
                    className="uppercase tracking-wide truncate"
                  >
                    {claim.client}
                  </Text>
                )}
              </div>
            </div>
            {/* `inPopoutWindow`: no "Ouvrir" / claim broadcast here (it is the
                sync receiver), and "Répondre" on an e-mail is sent to the main
                window's Email card over the popout channel (mahhp 22227). */}
            <DocumentsCard
              ses={claim.ses}
              idSin={claim.idSin}
              idEvent={claim.taskId}
              inPopoutWindow
            />
          </VStack>
        ) : (
          <div className="flex items-center justify-center h-[80vh]">
            <VStack gap={2} align="center">
              <FileText className="w-8 h-8 text-secondary-400" />
              <Text size="sm" color="muted">
                Aucune tâche sélectionnée
              </Text>
            </VStack>
          </div>
        )}
      </div>
    </RuleContextProvider>
  );
}
